import { axiosInstance } from '../../utils/axios'
import type {
  AdminApiKeyListResponse,
  AdminKeyStatusFilter,
  ApiKeyUsageResponse,
  AdminApiKey,
  CreatePerpetualKeyRequest,
  EligibleAccountsResponse,
  AdminApiKeyRequest,
  AdminApiKeyRequestListResponse,
} from '../../types/apiKeyAdmin'
import type { ApiKeyCreateResponse } from '../../types/apiKeys'

interface ApiResponse<T> {
  success: boolean
  data: T
}

const BASE_URL = '/admin/api-keys'

/**
 * Admin view over every account's API keys.
 *
 * Distinct from `apiKeysApi`: that one is scoped to the caller's own keys, this
 * one is global and is the incident-response path.
 */
export const apiKeysAdminApi = {
  list: async (
    status: AdminKeyStatusFilter = 'all',
    q = ''
  ): Promise<AdminApiKeyListResponse> => {
    const params = new URLSearchParams()
    if (status !== 'all') params.set('status', status)
    if (q.trim()) params.set('q', q.trim())
    const query = params.toString() ? `?${params}` : ''
    const res = await axiosInstance.get<ApiResponse<AdminApiKeyListResponse>>(
      `${BASE_URL}${query}`
    )
    return res.data.data
  },

  /**
   * Accounts a key may be issued for, filtered server-side by `q`.
   * Only returns accounts that can actually own a key, so anything offered
   * here will be accepted by `create`.
   */
  eligibleAccounts: async (q = ''): Promise<EligibleAccountsResponse> => {
    const query = q ? `?q=${encodeURIComponent(q)}` : ''
    const res = await axiosInstance.get<ApiResponse<EligibleAccountsResponse>>(
      `${BASE_URL}/eligible-accounts${query}`
    )
    return res.data.data
  },

  /** Traffic shape for one key over the last `days` days (server caps at 90). */
  usage: async (id: string, days = 7): Promise<ApiKeyUsageResponse> => {
    const res = await axiosInstance.get<ApiResponse<ApiKeyUsageResponse>>(
      `${BASE_URL}/${id}/usage?days=${days}`
    )
    return res.data.data
  },

  /** Soft delete: files the key away and revokes it. Row retained for audit. */
  remove: async (id: string): Promise<AdminApiKey> => {
    const res = await axiosInstance.post<ApiResponse<AdminApiKey>>(`${BASE_URL}/${id}/delete`)
    return res.data.data
  },

  revoke: async (id: string): Promise<AdminApiKey> => {
    const res = await axiosInstance.post<ApiResponse<AdminApiKey>>(`${BASE_URL}/${id}/revoke`)
    return res.data.data
  },

  /**
   * Mint a key on another account's behalf. The only path that can produce a
   * never-expiring key, and only with a justification, which is stored.
   */
  create: async (request: CreatePerpetualKeyRequest): Promise<ApiKeyCreateResponse> => {
    const res = await axiosInstance.post<ApiResponse<ApiKeyCreateResponse>>(
      BASE_URL,
      request
    )
    return res.data.data
  },

  /**
   * The perpetual-key request queue.
   *
   * Approving mints the key and returns its plaintext once, to the approving
   * admin — the requester never sees it in the app, so it has to be delivered
   * the same way a directly-issued key is.
   */
  requests: {
    list: async (status: 'pending' | 'all' = 'pending'): Promise<AdminApiKeyRequestListResponse> => {
      const query = status === 'all' ? '' : `?status=${status}`
      const res = await axiosInstance.get<ApiResponse<AdminApiKeyRequestListResponse>>(
        `${BASE_URL}/requests${query}`
      )
      return res.data.data
    },

    approve: async (id: string, overrideLimit = false): Promise<ApiKeyCreateResponse> => {
      const res = await axiosInstance.post<ApiResponse<ApiKeyCreateResponse>>(
        `${BASE_URL}/requests/${id}/approve`,
        overrideLimit ? { override_limit: true } : {}
      )
      return res.data.data
    },

    /** A reason is required — a bare refusal just produces the same request. */
    deny: async (id: string, denialReason: string): Promise<AdminApiKeyRequest> => {
      const res = await axiosInstance.post<ApiResponse<AdminApiKeyRequest>>(
        `${BASE_URL}/requests/${id}/deny`,
        { denial_reason: denialReason }
      )
      return res.data.data
    },
  },
}

export default apiKeysAdminApi
